import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    TextField, Grid, Card, CardMedia, CardContent, Typography,
    CircularProgress, MenuItem, Select, FormControl, InputLabel, Box
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { getApiUrl } from '../utils/apiConfig';

const CatalogBrowser = ({ open, onClose, suppliers, onImport }) => {
    const [selectedSupplierId, setSelectedSupplierId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filter suppliers that have Integration Configured
    const integratedSuppliers = suppliers.filter(s => s.api_url && s.consumer_key);

    const handleSearch = async () => {
        if (!selectedSupplierId) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const url = `${getApiUrl()}/api/suppliers/${selectedSupplierId}/catalog?search=${searchTerm}`;

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error('Falha ao buscar catálogo');

            const data = await response.json();
            setProducts(data.products || []);
        } catch (error) {
            console.error("Erro na busca:", error);
            alert("Erro ao buscar produtos. Verifique se as credenciais do fornecedor estão corretas.");
        } finally {
            setLoading(false);
        }
    };

    const handleImport = (product) => {
        // Primary Field: global_unique_id (User specified)
        let barcode = product.global_unique_id || '';

        // Secondary: Try to find Barcode/GTIN in meta_data (Common plugins use _gtin, _ean, gtin, ean)
        if (!barcode && product.meta_data) {
            const barcodeMeta = product.meta_data.find(m =>
                ['gtin', '_gtin', 'ean', '_ean', 'upc', '_upc', 'isbn', '_isbn', 'barcode'].includes(m.key.toLowerCase())
            );
            if (barcodeMeta) barcode = barcodeMeta.value;
        }

        // Fallback: Check top-level gtin/ean fields
        if (!barcode && product.gtin) barcode = product.gtin;
        if (!barcode && product.ean) barcode = product.ean;

        // Map WooCommerce product to our Inventory format
        const newItem = {
            nome: product.name,
            descricao: product.short_description ? product.short_description.replace(/<[^>]*>?/gm, '') : '', // Strip HTML
            categoria: 'consumivel', // Default
            unidade_medida: 'un',
            quantidade: 0,
            estoque_minimo: 5,
            codigo_barras: barcode,
            // You could map price or image if you had fields for it
            // If we had an image field: fotoUrl: product.images[0]?.src
        };
        onImport(newItem);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Catálogo de Fornecedores (WooCommerce)</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3, mt: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Fornecedor</InputLabel>
                                <Select
                                    value={selectedSupplierId}
                                    label="Fornecedor"
                                    onChange={(e) => setSelectedSupplierId(e.target.value)}
                                >
                                    {integratedSuppliers.map(s => (
                                        <MenuItem key={s.id} value={s.id}>{s.nome}</MenuItem>
                                    ))}
                                    {integratedSuppliers.length === 0 && (
                                        <MenuItem disabled>Nenhum fornecedor integrado</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Buscar produto..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<SearchIcon />}
                                onClick={handleSearch}
                                disabled={!selectedSupplierId || loading}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Buscar'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                <Grid container spacing={2}>
                    {products.map(product => (
                        <Grid item xs={12} sm={6} md={4} key={product.id}>
                            <Card variant="outlined">
                                {product.images && product.images.length > 0 && (
                                    <CardMedia
                                        component="img"
                                        height="140"
                                        image={product.images[0].src}
                                        alt={product.name}
                                        sx={{ objectFit: 'contain', p: 1 }}
                                    />
                                )}
                                <CardContent>
                                    <Typography variant="subtitle2" noWrap title={product.name}>
                                        {product.name}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        R$ {product.price}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        startIcon={<CloudDownloadIcon />}
                                        sx={{ mt: 1 }}
                                        onClick={() => handleImport(product)}
                                    >
                                        Importar
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {products.length === 0 && !loading && selectedSupplierId && (
                        <Grid item xs={12}>
                            <Typography align="center" color="textSecondary" sx={{ mt: 4 }}>
                                Nenhum produto encontrado ou busca não iniciada.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CatalogBrowser;
